import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'src/redux/index';
import { getEmployerResumeDownloadSettings } from 'src/helpers/utils';
import { uploadAnalyzeResume } from '../actions/xray';
import { sendJobInvite } from '../actions/inviteApplicant';
import {
  getOrgList,
  fetchNextOrg,
  checkJobDuplicateStatus,
  fetchJobByIdForEmployer,
  fetchOrgDetails,
  fetchPostedJobList,
  deleteJob,
  updateJob,
  uploadVerificationDocuments,
  getOrgJobStatistics,
  fetchOrgUsersList,
  removeOrgMember,
  changeOrgUserRole,
  updateOrg,
  deleteOrg,
  getOrgTransactions,
  getStoreItemsByID,
} from '../actions/employerLayout';
import {
  EmployerLayoutInitialState,
  ResumeDownloadSettings,
} from '../types/employerLayout';

const initialState: EmployerLayoutInitialState = {
  fetchingJobsFirstPage: true,
  postedJobList: null,
  activeOrgId: null,
  verificationDocUrl: '',
  jobSearchQuery: '',
  orgJobsStatistics: null,
  fetchingOrgJobsStats: true,
  activeOrgDetails: null,
  isRetryInProgress: false,
  isCheckingDuplicateJobState: false,
  orgList: null,
  orgUsersList: null,
  orgTransactions: null,
  currentPlan: null,
  resumeDownloadSettings: getEmployerResumeDownloadSettings(),
};

/* eslint-disable no-param-reassign */
const employerSlice = createSlice({
  name: 'Employer',
  initialState,
  reducers: {
    setJobSearchQuery: (state, action: PayloadAction<string>) => {
      state.jobSearchQuery = action.payload;
    },
    setActiveOrg: (state, action: PayloadAction<string>) => {
      if (action.payload !== 'all-orgs') {
        state.orgUsersList = null;
        state.activeOrgId = action.payload;
        localStorage.setItem('activeOrgId', action.payload);
        if (state.orgList?.org_list) {
          state.activeOrgData = state.orgList.org_list?.find(
            (val) => val.id === action.payload,
          );
        }
      }
    },
    resetActiveOrg: (state) => {
      state.activeOrgId = null;
      state.activeOrgData = null;
      localStorage.removeItem('activeOrgId');
    },
    setActiveOrgVerificationDocUrl: (state, action) => {
      (state.activeOrgData as any).verification_doc_url = action.payload;
    },
    incrementOrgMonthlyJobPostCount: (state) => {
      if (state.activeOrgDetails) {
        state.activeOrgDetails.monthly_job_posted =
          (state.activeOrgDetails.monthly_job_posted ?? 0) + 1;
      }
    },
    setDashboardJobStatus: (state, action: PayloadAction<string>) => {
      if (state.dashboardJobDetails?.job) {
        state.dashboardJobDetails.job.status = action.payload;
      }
    },
    setResumeDownloadSettings: (
      state,
      action: PayloadAction<ResumeDownloadSettings>,
    ) => {
      state.resumeDownloadSettings = action.payload;
    },
  },
  extraReducers: (builder) => {
    // An invite sent from the dashboard (or the invite page) counts towards
    // the open job's Invited stat straight away.
    builder.addCase(sendJobInvite.fulfilled, (state, action) => {
      const job = state.dashboardJobDetails?.job;
      if (job?.id === action.meta.arg.jobId) {
        job.total_invited = (job.total_invited ?? 0) + 1;
      }
    });
    builder
      .addCase(fetchPostedJobList.pending, (state, action) => {
        if (action.meta.arg.pageNo === 1) {
          state.fetchingJobsFirstPage = true;
        }
      })
      .addCase(fetchPostedJobList.fulfilled, (state, action) => {
        if (action.payload.page_no === 1) {
          state.postedJobList = action.payload;
        } else if (state.postedJobList) {
          state.postedJobList.jobs = [
            ...(state.postedJobList?.jobs ?? []),
            ...(action.payload.jobs ?? []),
          ];
          state.postedJobList.page_no = action.payload.page_no;
          state.postedJobList.page_size = action.payload.page_size;
          state.postedJobList.total_pages = action.payload.total_pages;
          state.postedJobList.total_jobs = action.payload.total_jobs;
          state.postedJobList.total_unfiltered_jobs =
            action.payload.total_unfiltered_jobs;
        }
        state.fetchingJobsFirstPage = false;
      })
      .addCase(fetchPostedJobList.rejected, (state) => {
        state.fetchingJobsFirstPage = false;
      });
    builder.addCase(deleteJob.fulfilled, (state, action) => {
      const jobData = state.postedJobList?.jobs?.find(
        (job) => job.id === action.meta.arg.jobId,
      );

      if (state?.postedJobList?.jobs) {
        state.postedJobList.jobs = state.postedJobList.jobs.filter(
          (job) => job.id !== action.meta.arg.jobId,
        );
      }

      if (!jobData || !state.orgJobsStatistics) {
        return;
      }

      if (
        jobData.is_gpt_job &&
        state.orgJobsStatistics.bulk_analysis?.total_count
      ) {
        state.orgJobsStatistics.bulk_analysis.total_count -= 1;
      } else if (state.orgJobsStatistics.jobs?.total_count) {
        state.orgJobsStatistics.jobs.total_count -= 1;

        if (
          state.orgJobsStatistics.jobs.draft_count &&
          jobData.status === 'Draft'
        ) {
          state.orgJobsStatistics.jobs.draft_count -= 1;
        }

        if (
          state.orgJobsStatistics.jobs.under_review_count &&
          jobData.status === 'UnderReview'
        ) {
          state.orgJobsStatistics.jobs.under_review_count -= 1;
        }
      }

      if (
        jobData.status === 'Rejected' &&
        state.activeOrgDetails?.monthly_job_posted
      ) {
        state.activeOrgDetails.monthly_job_posted -= 1;
      }
    });
    builder.addCase(fetchNextOrg.fulfilled, (state, action) => {
      if (
        state.orgList?.page &&
        state.orgList?.org_list &&
        action.payload.org_list
      ) {
        state.orgList.org_list = [
          ...state.orgList.org_list,
          ...action.payload.org_list,
        ];
        state.orgList.page = action.payload.page;
      }
      if (!state.activeOrgId) {
        const savedActiveOrgId = localStorage.getItem('activeOrgId');
        if (
          savedActiveOrgId &&
          savedActiveOrgId?.trim().length > 0 &&
          action.payload.org_list?.findIndex(
            (val) => val.id === savedActiveOrgId,
          ) !== -1
        ) {
          state.activeOrgId = savedActiveOrgId;
          state.activeOrgData = action.payload?.org_list?.find(
            (val) => val.id === savedActiveOrgId,
          );
        } else if (action.payload?.org_list?.[0]?.id) {
          const orgData = action.payload?.org_list?.[0];
          state.activeOrgId = orgData?.id ?? '';
          state.activeOrgData = orgData;
          localStorage.setItem('activeOrgId', orgData.id!);
        }
      } else {
        state.activeOrgData = [
          ...(state?.orgList?.org_list || []),
          ...(action.payload.org_list || []),
        ]?.find((val) => val.id === state.activeOrgId);
      }
    });

    builder.addCase(getOrgList.fulfilled, (state, action) => {
      if ((action.payload.data.page ?? 1) > 1) {
        const updateOrgList = [
          ...(state.orgList?.org_list ?? []),
          ...(action.payload.data?.org_list ?? []),
        ];
        state.orgList = {
          ...state.orgList,
          org_list: updateOrgList,
          page: action.payload.data.page,
          page_no: action.payload.data.page_no,
          total_pages: action.payload.data.total_pages,
        };
      } else {
        state.orgList = action.payload.data;
      }
      if (!state.activeOrgId || action.payload.shouldSelectFirstOrg) {
        const savedActiveOrgId = localStorage.getItem('activeOrgId');
        if (
          savedActiveOrgId &&
          savedActiveOrgId?.trim().length > 0 &&
          state.orgList.org_list?.findIndex(
            (val) => val.id === savedActiveOrgId,
          ) !== -1
        ) {
          state.activeOrgId = savedActiveOrgId;
          state.activeOrgData = action.payload.data.org_list?.find(
            (val) => val.id === savedActiveOrgId,
          );
        } else if (
          action.payload.shouldSelectFirstOrg &&
          action.payload.data.org_list?.[0]?.id
        ) {
          const orgData = action.payload.data.org_list?.[0];
          state.activeOrgId = orgData?.id ?? '';
          state.activeOrgData = orgData;
          localStorage.setItem('activeOrgId', orgData.id!);
        }
      } else {
        state.activeOrgData = state.orgList.org_list?.find(
          (val) => val.id === state.activeOrgId,
        );
      }
    });

    builder.addCase(uploadVerificationDocuments.fulfilled, (state, action) => {
      state.verificationDocUrl = action.payload.url || '';
    });
    builder.addCase(getOrgJobStatistics.pending, (state) => {
      state.fetchingOrgJobsStats = true;
    });
    builder.addCase(getOrgJobStatistics.fulfilled, (state, action) => {
      state.orgJobsStatistics = action.payload;
      state.fetchingOrgJobsStats = false;
    });
    builder.addCase(getOrgJobStatistics.rejected, (state) => {
      state.fetchingOrgJobsStats = false;
    });
    builder.addCase(fetchJobByIdForEmployer.fulfilled, (state, action) => {
      state.dashboardJobDetails = action.payload;
    });
    builder.addCase(checkJobDuplicateStatus.pending, (state) => {
      state.duplicateJobResponse = {};
      state.isCheckingDuplicateJobState = true;
    });
    builder.addCase(checkJobDuplicateStatus.fulfilled, (state, action) => {
      state.duplicateJobResponse = action.payload;
      state.isCheckingDuplicateJobState = false;
    });
    builder.addCase(checkJobDuplicateStatus.rejected, (state) => {
      state.duplicateJobResponse = {};
      state.isCheckingDuplicateJobState = true;
    });
    builder.addCase(fetchOrgUsersList.fulfilled, (state, action) => {
      state.orgUsersList = action.payload;
    });
    builder.addCase(changeOrgUserRole.fulfilled, (state, action) => {
      const { arg } = action.meta;
      if (state.orgUsersList) {
        state.orgUsersList = state.orgUsersList.map((orgUser) => {
          if (orgUser.user_id === arg.userId && orgUser.org_id === arg.orgId) {
            return { ...orgUser, role: arg.role };
          }
          return orgUser;
        });
      }
    });
    builder.addCase(removeOrgMember.fulfilled, (state, action) => {
      if (state.orgUsersList && state.activeOrgId === action.meta.arg.orgId) {
        state.orgUsersList = state.orgUsersList.filter(
          (user) => user.email !== action.meta.arg.email,
        );
      }
    });
    builder.addCase(fetchOrgDetails.fulfilled, (state, action) => {
      state.activeOrgDetails = action.payload;
    });
    builder.addCase(updateJob.fulfilled, (state, action) => {
      if (state.postedJobList?.jobs) {
        state.postedJobList.jobs = state.postedJobList.jobs.map((job) => {
          if (job.id === action.payload.id) {
            return { ...action.payload, is_archieved: undefined };
          }
          return job;
        });
      }
    });
    builder.addCase(updateOrg.fulfilled, (state, action) => {
      state.activeOrgData = action.payload;
    });
    builder.addCase(deleteOrg.fulfilled, (state, action) => {
      if (!state.orgList?.org_list) {
        return;
      }
      const deletedOrgId = action.meta.arg.orgId;
      state.orgList.org_list = state.orgList.org_list.filter(
        (org) => org.id !== deletedOrgId,
      );
      if (state.activeOrgId === deletedOrgId && state.orgList.org_list.length) {
        const newActiveOrg = state.orgList.org_list[0];
        state.orgUsersList = null;
        state.activeOrgDetails = null;
        state.activeOrgId = newActiveOrg.id;
        localStorage.setItem('activeOrgId', newActiveOrg.id ?? '');
        state.activeOrgData = newActiveOrg;
      } else if (state.activeOrgId === deletedOrgId) {
        state.orgUsersList = null;
        state.activeOrgDetails = null;
        state.activeOrgData = null;
        localStorage.removeItem('activeOrgId');
      }
    });
    builder.addCase(getOrgTransactions.fulfilled, (state, action) => {
      state.orgTransactions = action.payload;
    });
    builder.addCase(getStoreItemsByID.fulfilled, (state, action) => {
      state.currentPlan = action.payload[0] ?? [];
    });
    builder.addCase(uploadAnalyzeResume.fulfilled, (state, action) => {
      if (
        action.payload.error !== 'Resume already exists' &&
        state.activeOrgDetails
      ) {
        state.activeOrgDetails.monthly_resume_upload_count =
          (state.activeOrgDetails?.monthly_resume_upload_count ?? 0) + 1;
      }
    });
  },
});
/* eslint-enable no-param-reassign */

const { reducer, actions } = employerSlice;

export default reducer;
export const employerSelector = (state: RootState) => state.employer;
export const orgListSelector = (state: RootState) => state.employer.orgList;
export const {
  setActiveOrg,
  setJobSearchQuery,
  resetActiveOrg,
  setActiveOrgVerificationDocUrl,
  incrementOrgMonthlyJobPostCount,
  setResumeDownloadSettings,
  setDashboardJobStatus,
} = actions;
